//search key properties
import { SOCIAL_MEDIA_METADATA } from '../../common/metadata';
import { v4 as uuidv4 } from 'uuid';

export const searchTerm = {
  masterSearch: 'name',
  searchTermTwitter: 'twitterHandler',
  searchTermFacebook: 'facebookHandler',
  searchTermYoutube: 'youtubeHandler',
  searchTermInstagram: 'instagramHandler',
  searchTermReddit: 'redditHandler',
  searchTermNews: 'newsHandler',
  searchTermLinkedIn: 'linkedinPublicPagePHandler',
  searchTermTikTok: 'tiktokHandler',
  searchTermTrustPilot: 'trustPilotcompanyName',
};

// alert key properties
const ALERT_KEYS = {
  keywordDetectionAlert: {
    alertTwitter: { key: 'tweetKeyword', frequency: 'twitterKeywordfrequency' },
    alertFacebook: {
      key: 'facebookHandlerKeyword',
      frequency: 'facebookHandlerKeywordfrequency',
    },
    alertYoutube: {
      key: 'youtubeKeyword',
      frequency: 'youtubeKeywordfrequency',
    },
    alertInstagram: {
      key: 'instagramKeyword',
      frequency: 'instagramKeywordfrequency',
      subtype: 'subtype',
    },
    alertReddit: { key: 'redditKeyword', frequency: 'redditKeywordfrequency' },
    alertNews: { key: 'newsKeyword', frequency: 'newsKeywordfrequency' },
    alertTikTok: {
      key: 'tiktokKeyword',
      frequency: 'tiktokKeywordfrequency',
      subtype: 'subtype',
    },
    alertLinkedIn: {
      key: 'linkedinKeyword',
      frequency: 'linkedinKeywordfrequency',
    },
    alertlinkedinpublicpage: {
      key: 'linkedinPublicPageKeyword',
      frequency: 'linkedinPublicPageKeywordfrequency',
    },
  },
  averageSentimentDropAlert: {
    alertTwitter: {
      key: 'tweetAverageSentimentValue',
      frequency: 'twitterSentimentDropfrequency',
    },
    alertFacebook: {
      key: 'facebookAverageSentimentValue',
      frequency: 'facebookSentimentDropfrequency',
    },
    alertYoutube: {
      key: 'youtubeAverageSentimentValue',
      frequency: 'youtubeSentimentDropfrequency',
    },
    alertInstagram: {
      key: 'instagramAverageSentimentValue',
      frequency: 'instagramSentimentDropfrequency',
      subtype: 'subtype',
    },
    alertReddit: {
      key: 'redditAverageSentimentValue',
      frequency: 'redditSentimentDropfrequency',
    },
    alertNews: {
      key: 'newsAverageSentimentValue',
      frequency: 'newsSentimentDropfrequency',
    },
    alertTikTok: {
      key: 'tiktokAverageSentimentValue',
      frequency: 'tiktokSentimentDropfrequency',
      subtype: 'subtype',
    },
    alertLinkedIn: {
      key: 'linkedinAverageSentimentValue',
      frequency: 'linkedinSentimentDropfrequency',
    },
    alertlinkedinpublicpage: {
      key: 'linkedinPublicPageAverageSentimentValue',
      frequency: 'linkedinPublicPageSentimentDropfrequency',
    },
  },
  countSentimentAlert: {
    alertTwitter: {
      key: 'twitterCountSentiment',
      frequency: 'twitterCountfrequency',
      threshold: 'threshold',
    },
    alertFacebook: {
      key: 'facebookCountSentiment',
      frequency: 'facebookCountfrequency',
      threshold: 'threshold',
    },
    alertYoutube: {
      key: 'youtubeCountSentiment',
      frequency: 'youtubeCountfrequency',
      threshold: 'threshold',
    },
    alertInstagram: {
      key: 'instagramCountSentiment',
      frequency: 'instagramCountfrequency',
      threshold: 'threshold',
      subtype: 'subtype',
    },
    alertReddit: {
      key: 'redditCountSentiment',
      frequency: 'redditCountfrequency',
      threshold: 'threshold',
    },
    alertNews: {
      key: 'newsCountSentiment',
      frequency: 'newsCountfrequency',
      threshold: 'threshold',
    },
    alertTikTok: {
      key: 'tiktokCountSentiment',
      frequency: 'tiktokCountfrequency',
      threshold: 'threshold',
      subtype: 'subtype',
    },
    alertLinkedIn: {
      key: 'linkedinCountSentiment',
      frequency: 'linkedinCountfrequency',
      threshold: 'threshold',
    },

    alertlinkedinpublicpage: {
      key: 'linkedinPublicPageCountSentiment',
      frequency: 'linkedinPublicPageCountfrequency',
      threshold: 'threshold',
    },
  },
};

export const ALERT_KEYS_TYPE = {
  keywordDetection: 'keywordDetectionAlert',
  averageSentimentDrop: 'averageSentimentDropAlert',
  countSentimentAlert: 'countSentimentAlert',
};

export const ALERT_KEYS_VALUE = {
  keywordDetectionAlert: 'Keyword Detection',
  averageSentimentDropAlert: 'Sentiment Drop',
  countSentimentAlert: 'Count Sentiment Limit',
};

export const FrequencyListNames = {
  hourly: 'Hourly',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

// category key properties
const CATEGORY_MAPS = {
  categoryTwitter: 'twitterHandler',
  categoryFacebook: 'facebookHandler',
  categoryInstagram: 'instagramHandler',
  categoryYoutube: 'youtubeHandler',
  categoryReddit: 'redditHandler',
  categoryNews: 'newsHandler',
  categoryTikTok: 'tiktokHandler',
  categoryLinkedIn: 'linkedinHandler',
  categoryTrustPilot: 'trustPilotcompanyName',
};

const recordTime = (company) => {
  const t = Date.parse(company?.modifiedTime || company?.createTime || '');
  return Number.isNaN(t) ? 0 : t;
};

// One company per id. The backend can hold the same company twice (an edit made while
// the app still created a new record instead of updating the old one left both with the
// same id); the list then showed it twice, the group tab counted it twice, and - because
// the cards are keyed by id - switching group tabs left stale copies of it on screen.
// Of the records that share an id the most recently modified one wins (the user's last
// edit), at the position where the id first appeared. Records without an id are all kept.
export const dedupeCompaniesById = (companies) => {
  const list = Array.isArray(companies) ? companies : [];
  const winnerById = new Map();
  list.forEach((company) => {
    const id = company?.id;
    if (id === undefined || id === null || id === '') return;
    const current = winnerById.get(id);
    if (!current || recordTime(company) > recordTime(current)) winnerById.set(id, company);
  });
  const placed = new Set();
  return list.reduce((acc, company) => {
    const id = company?.id;
    if (id === undefined || id === null || id === '') {
      acc.push(company);
    } else if (!placed.has(id)) {
      placed.add(id);
      acc.push(winnerById.get(id));
    }
    return acc;
  }, []);
};

export const companiesResponseTransformer = (data, favData) => {
  const { companies: rawCompanies = [], ...profileKeys } = data;
  const companies = dedupeCompaniesById(rawCompanies);
  const profileObject = { ...profileKeys };
  let tempAlertMap;
  delete profileObject['Account name'];
  profileObject.user = profileKeys.user;
  profileObject.accountname = profileKeys['Account name'];
  // Parse Companies
  const companiesCollection = companies.map((company) => {
    const compCollector = {};
    const mediaSearchKeys = {};
    const { alerts = [], categories = [] } = company;
    compCollector.masterSearch = company[searchTerm.masterSearch];
    compCollector.id = company.id;
    compCollector.language = company['lang'];
    compCollector.tiktokType = company?.tiktokSearches;
    compCollector.instagramType = company?.instagramSearches;
    compCollector.tiktokOptions = company?.tiktokType;
    compCollector.instagramOptions = company?.instagramType;
    compCollector.youtubeOptions = company?.youtubeType;
    compCollector.youtubeType = company?.youtubeSearches;
    // Mirrors tiktokType/instagramType/youtubeType above — the "Also fetch
    // posts mentioning..." checkbox state saved via companies.service.js's
    // `linkedin` field comes back here as `linkedinSearches`.
    compCollector.linkdinType = company?.linkedinSearches;
    compCollector.group_name = company?.group_name;
    compCollector.categories = company?.categories;
    compCollector.facebookScreenName = company?.facebookScreenName;
    // Raw alerts (already in the POST/editCompany shape) preserved so an
    // editCompany save can resend them unchanged and not wipe existing alerts.
    compCollector.rawAlerts = Array.isArray(company?.alerts)
      ? company.alerts
      : [];
    // Saved keyword sets ({ mainKeyword, suggestedKeywords[] }) for the
    // Vision Insights keyword editor; read back so rows repopulate on reload.
    compCollector.keywords = Array.isArray(company?.keywords)
      ? company.keywords
      : [];
    // User-defined "categories of analysis" (e.g. "disability",
    // "accessibility") used to AI-tag fetched posts by relevance. Simple
    // string list, company-wide (not per-channel like the alerts
    // `categories` field above) — read back so the input repopulates.
    compCollector.analysisCategories = Array.isArray(company?.analysisCategories)
      ? company.analysisCategories
      : [];

    const dataNew = [
      ...SOCIAL_MEDIA_METADATA,
      {
        id: '10',
        icon: 'linkedinpublicpage',
        label: 'linkedinpublicpage',
        state: [
          {
            isValid: false,
            route: '/brandintel/trackcompanies',
            isVisible: true,
          },
          { isValid: false, route: '/brandintel/dashboard', isVisible: false },
          {
            isValid: false,
            route: '/brandintel/comparisonhub',
            isVisible: false,
          },
          { isValid: true, route: '/profile', isVisible: true },
          { isValid: true, route: '/profile/subscription', isVisible: true },
          { isValid: true, route: '/linkedin/insights/', isVisible: true },
          { isValid: false, route: '/livesearch', isVisible: false },
          { isValid: false, route: '/genai-live', isVisible: false },
          // { isValid: false, route: "/haix-genai/summary", isVisible: true },
        ],
      },
    ];

    compCollector.socialInfo = dataNew.map((item) => {
      tempAlertMap = [];
      const mediaCollector = {};
      mediaCollector.type = item['icon'];
      mediaCollector.id = uuidv4();

      if (item.label == 'Youtube') {
        mediaSearchKeys[item.label.toLowerCase()] =
          company?.youtubeSearches?.channel?.trim() !== ''
            ? company.youtubeSearches?.channel
            : company?.youtubeSearches?.keyword?.trim() !== ''
            ? company.youtubeSearches.keyword
            : undefined;
        mediaCollector.searchTerm =
          company?.youtubeSearches?.channel?.trim() !== ''
            ? company.youtubeSearches?.channel
            : company?.youtubeSearches?.keyword?.trim() !== ''
            ? company.youtubeSearches.keyword
            : undefined;
      } else if (item.label == 'LinkedIn') {
        // Use handler (vanity name) if available, fall back to URL (for individual profiles)
        const linkedinKey =
          company?.linkedinPublicPageHandler?.trim()
            ? company.linkedinPublicPageHandler.trim()
            : company?.linkedinPublicPageUrl?.trim()
            ? company.linkedinPublicPageUrl.trim()
            : undefined;
        mediaSearchKeys[item.label.toLowerCase()] = linkedinKey;
        mediaCollector.searchTerm = linkedinKey;
      } else if (item.label == 'Instagram') {
        mediaSearchKeys[item.label.toLowerCase()] =
          company?.instagramSearches?.hashtag?.trim() !== ''
            ? company.instagramSearches?.hashtag
            : company?.instagramSearches?.manual_keywords?.trim() !== ''
            ? company.instagramSearches?.manual_keywords
            : company?.instagramSearches?.userAccount?.trim() !== ''
            ? company.instagramSearches?.userAccount
            : company?.instagramSearches?.userTag?.trim() !== ''
            ? company.instagramSearches?.userTag
            : undefined;

        mediaCollector.searchTerm =
          company?.instagramSearches?.hashtag?.trim() !== ''
            ? company.instagramSearches?.hashtag
            : company?.instagramSearches?.manual_keywords?.trim() !== ''
            ? company.instagramSearches?.manual_keywords
            : company?.instagramSearches?.userAccount?.trim() !== ''
            ? company.instagramSearches?.userAccount
            : company?.instagramSearches?.userTag?.trim() !== ''
            ? company.instagramSearches?.userTag
            : undefined;
      } else {
        mediaSearchKeys[item.label.toLowerCase()] =
          company[searchTerm[`searchTerm${item.label}`]];
        mediaCollector.searchTerm =
          company[searchTerm[`searchTerm${item.label}`]];
      }

      // alerts code start
      for (const alert of alerts) {
        const alertType = alert['alertType'];
        if (alert[ALERT_KEYS[alertType][`alert${item.label}`]?.key]) {
          const alertCollector = {};
          alertCollector.type = alertType;
          alertCollector.subtype =
            alert[ALERT_KEYS?.[alertType]?.[`alert${item.label}`]?.['subtype']];
          alertCollector.id = uuidv4();
          try {
            alertCollector.keyword =
              alert[ALERT_KEYS[alertType][`alert${item.label}`]['key']];
            alertCollector.frequency =
              alert[ALERT_KEYS[alertType][`alert${item.label}`]['frequency']];
            if (alertType === ALERT_KEYS_TYPE.countSentimentAlert)
              alertCollector.threshold =
                alert[ALERT_KEYS[alertType][`alert${item.label}`]['threshold']];
          } catch (e) {
            console.error('error occured');
          }
          tempAlertMap.push(alertCollector);
        }
      }
      mediaCollector.alerts = [...tempAlertMap];
      // alerts code end
      // ;
      // categories code start
      // Two shapes possible per entry:
      //  - New: { categoryName, categoryKeywords, subtype? } — flat object.
      //    subtype present = an Analysis Category (see ManageCompaniesView.jsx
      //    getCategoriesData()); subtype absent = a Mapped Category.
      //  - Legacy: { [categoryName]: categoryKeywords } single-key map, from
      //    before the flat shape — kept for backward compatibility with
      //    already-saved data.
      if (categories[CATEGORY_MAPS[`category${item.label}`]]) {
        mediaCollector.categories = categories[
          CATEGORY_MAPS[`category${item.label}`]
        ].map((category) => {
          if (category && category.categoryName !== undefined) {
            return {
              category: category.categoryName,
              id: uuidv4(),
              keyword: category.categoryKeywords || '',
              subtype: category.subtype || '',
              tiktokType: '',
              instagramType: '',
              youtubeType: '',
            };
          }
          const legacyKey = Object.keys(category || {})[0];
          return {
            category: legacyKey,
            id: uuidv4(),
            keyword: category?.[legacyKey],
            tiktokType: '',
            instagramType: '',
            youtubeType: '',
          };
        });
      }

      // categories code end
      return mediaCollector;
    });
    compCollector.mediaSearchKeys = mediaSearchKeys;
    return compCollector;
  });
  profileObject.companies = companiesCollection;
  profileObject.companyGroup = profileKeys['company_group'];
  profileObject.favourites = favData?.favourites;
  //TODO:: add other params needed throughout the app for userprofile
  return profileObject;
};

/* Sample Schema */
// {
//   type: "fb-simple",
//   searchTerm: "searchterm1",
//   alerts: [
//     {
//       type: "keywordDetection",
//       keyword: "test tewnety tert",
//       frequency: "daily",
//     },
//     {
//       type: "sentimentDrop",
//       keyword: "tsrs sdfsf fsdf",
//       frequency: "monthly",
//     },
//     {
//       type: "countSentimentAlert",
//       keyword: "crazy test",
//       frequency: "yearly",
//     },
//   ],
//   categories: [
//     { category: "orbutuary", keyword: "one nice skrty" },
//     { category: "gate keeping", keyword: "suel fonarty" },
//   ],
// }
/* Sample Schema */
